import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * MM-1207: parses an appointment date-time from the API as UTC. The API sends UTC without a zone
 * ("yyyy-MM-dd HH:mm:ss"); new Date() would read it as browser-local time and shift the day and the
 * instant by the browser's UTC offset. Returns null for an empty or invalid value.
 */
export const parseAppointmentUtc = (value: any) => {
  if (!value) {
    return null;
  }
  const utcValue = dayjs.utc(String(value).substring(0, 23));
  return utcValue.isValid() ? utcValue : null;
};

/**
 * MM-1207: the appointment date-time in the browser's time zone (the patient's login), so the
 * date, month, year and time all come from the same value and match Appointment History.
 * Returns null for an empty or invalid value.
 */
export const toLocalAppointmentTime = (value: any) => {
  const utcValue = parseAppointmentUtc(value);
  return utcValue ? utcValue.local() : null;
};

/**
 * MM-1207: whether the appointment falls on today's date in the browser's time zone.
 */
export const isAppointmentOnLocalToday = (value: any) => {
  const localTime = toLocalAppointmentTime(value);
  return !!localTime && localTime.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD');
};

export const formatDateTime = (dateValue: any) => {
  const localDate = dayjs.utc(dateValue).local().format('MM/DD/YYYY');
  const localTime = dayjs.utc(dateValue).local().format('hh:mm A');
  return dateValue && localDate && localTime ? localDate + ' - ' + localTime : '';
};

export const formatAppointmentDateTime = (dateValue: any) => {
  const localDate = dateValue ? dayjs?.utc(dateValue)?.local()?.format('MMM DD, YYYY') : '';
  const str = dateValue ? dateValue?.substring(0, 23) : '';
  const localTime = dateValue ? dayjs?.utc(str)?.local()?.format('hh:mm A') : '';
  return dateValue && localDate && localTime ? localDate + ' - ' + localTime : '';
};

export const formatNumber = (numberValue: any) => {
  return isNaN(numberValue) ? '' : numberValue;
};

export const formatDate = (value: any) => {
  if (dayjs.utc(value).local().format('MM/DD/YYYY') === 'Invalid Date') {
    return '';
  } else {
    return dayjs.utc(value).local().format('MM/DD/YYYY');
  }
};

// MM-1177: task due dates are shown in the browser's timezone. Due dates saved without a
// timezone (older tasks and auto-created tasks) are stored at exactly midnight UTC, so those
// are read as a calendar date to show the same day everywhere.
const toTaskDueDate = (value: any) => {
  if (!value) {
    return null;
  }
  const utcValue = dayjs.utc(value);
  if (!utcValue.isValid()) {
    return null;
  }
  const isDateOnly =
    utcValue.hour() === 0 && utcValue.minute() === 0 && utcValue.second() === 0 && utcValue.millisecond() === 0;
  return isDateOnly ? dayjs(utcValue.format('YYYY-MM-DD')) : utcValue.local();
};

export const formatTaskDueDate = (value: any) => {
  const dueDate = toTaskDueDate(value);
  return dueDate ? dueDate.format('MM/DD/YYYY') : '';
};

export const formatPhoneNumber = (phoneNumber: any) => {
  if (phoneNumber == null) {
    return '';
  }
  const value = String(phoneNumber);
  const cleaned = ('' + value).replace(/\D/g, '');
  let match: any = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match === null && cleaned.length === 11) {
    match = cleaned.slice(1).match(/^(\d{3})(\d{3})(\d{4})$/);
  }

  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  return null;
};

export const normalizePhone = (phone: any): string => {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, ''); // keep only digits
  return digits.length > 10 ? digits.slice(-10) : digits; // last 10 digits
};

export const safeString = (value: string): string => (value == null ? '' : String(value));
